import { Box, Stack, Typography } from "@mui/material";

interface SnapshotMissingErrorProps {
  /** The traceable error code returned by the server (e.g. ECSN-54123). */
  errorCode: string;
  /**
   * Variant determines copy:
   * - "customer": neutral support-facing message
   * - "staff":    direct internal message naming the snapshot specifically
   */
  variant?: "customer" | "staff";
}

/**
 * Rendered when the report's `tierIIReport` query throws the missing-snapshot
 * error code. Always shows the code so support tickets are easy to trace
 * back to logs / Sentry.
 */
export const SnapshotMissingError = ({
  errorCode,
  variant = "customer",
}: SnapshotMissingErrorProps) => {
  const title = variant === "staff" ? "This report's snapshot is missing" : "Something went wrong";
  const subtext =
    variant === "staff"
      ? "The report needs to be re-snapshotted. Contact engineering support and reference the error code below."
      : "Please contact support and include the error code below so we can look into it.";

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flex={1}
      sx={{ minHeight: "60vh", px: 3 }}
    >
      <Stack spacing={3} alignItems="center" maxWidth={480} textAlign="center">
        <img
          src="/oops_hiker.png"
          alt=""
          style={{ height: 160, maxWidth: 200 }}
          aria-hidden="true"
        />
        <Typography variant="h5" component="h1" sx={{ fontWeight: 300 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {subtext}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            px: 2,
            py: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          Error code: {errorCode}
        </Typography>
      </Stack>
    </Box>
  );
};
